import { React, useEffect, useState, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Paper, CircularProgress, Backdrop, Typography } from '@mui/material';
import {
    DataGridPro,
    GridToolbar,
    GridFooterContainer,
    GridFooter
} from '@mui/x-data-grid-pro';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { socket } from '../services/socket';
import { formatCurrency, formatNumber } from '../utils/utils.ts';
import { usePrevious } from '../hooks/usePrevious.ts';

dayjs.extend(utc);
dayjs.extend(timezone);

var api = axios.create({ withCredentials: true });

const columns = [
    { field: 'invoice', headerName: 'Invoice', flex: .4, minWidth: 100, type: 'string' },
    { field: 'event_date', headerName: 'Event Date', flex: .5, minWidth: 150, type: 'dateTime' },
    { field: 'event_name', headerName: 'Event', flex: 1, minWidth: 180, type: 'string' },
    { field: 'qty', headerName: 'Qty', flex: .25, minWidth: 80, type: 'number' },
    { field: 'section', headerName: 'Section', flex: .35, minWidth: 90, type: 'string' },
    { field: 'row', headerName: 'Row', flex: .25, minWidth: 70, type: 'string' },
    {
        field: 'sales', headerName: 'Ticket Sale', flex: .4, minWidth: 110, type: 'number',
        renderCell: (params) => `${formatCurrency(params.value)}`
    },
    {
        field: 'costs', headerName: 'Cost', flex: .4, minWidth: 110, type: 'number',
        renderCell: (params) => `${formatCurrency(params.value)}`
    },
    {
        field: 'profit', headerName: 'Profit/Loss', flex: .4, minWidth: 110, type: 'number',
        renderCell: (params) => `${formatCurrency(params.value)}`
    },
    { field: 'customer', headerName: 'Customer', flex: .7, minWidth: 150, type: 'string' },
];

function HourlyRegister({ isConnected, current_broker }) {
    const { getAccessTokenSilently } = useAuth0();
    
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());
    const previous_broker = usePrevious(current_broker);

    const totals = useMemo(() => {
        return rows.reduce((acc, r) => {
            acc.qty += Number(r.qty) || 0;
            acc.sales += Number(r.sales) || 0;
            acc.costs += Number(r.costs) || 0;
            acc.profit += Number(r.profit) || 0;
            return acc;
        }, { qty: 0, sales: 0, costs: 0, profit: 0 });
    }, [rows]);

    function CustomFooter() {
        return (
            <GridFooterContainer>
                <Box sx={{ display: 'flex', gap: 3, px: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2"><strong>Qty:</strong> {formatNumber(totals.qty)}</Typography>
                    <Typography variant="body2"><strong>Sales:</strong> {formatCurrency(totals.sales)}</Typography>
                    <Typography variant="body2"><strong>Cost:</strong> {formatCurrency(totals.costs)}</Typography>
                    <Typography variant="body2"><strong>Profit/Loss:</strong> {formatCurrency(totals.profit)}</Typography>
                </Box>
                <GridFooter sx={{ border: 'none' }} />
            </GridFooterContainer>
        );
    }

    function getData(controller) {
        getAccessTokenSilently({
            audience: process.env.REACT_APP_AUTH0AUDIENCE,
            scope: process.env.REACT_APP_AUTH0SCOPE
        }).then((token) => {
            const startParam = startDate && startDate.isValid() ? `&start=${startDate.format('YYYY-MM-DD')}` : '';
            const endParam = endDate && endDate.isValid() ? `&end=${endDate.format('YYYY-MM-DD')}` : '';
            api.get(
                `${process.env.REACT_APP_BASEURL}/api/datagrid/hourly/${current_broker}?timezone=${encodeURIComponent(dayjs.tz.guess())}${startParam}${endParam}`,
                { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } }
            ).then((response) => {
                var r = [];
                response.data.forEach((doc, i) => {
                    r.push({
                        id: i,
                        invoice: doc._id.invoice,
                        event_date: doc._id.event_date ? dayjs(doc._id.event_date.replace(/[Z]$/g, '')).toDate() : null,
                        event_name: doc._id.event_name,
                        qty: doc.count,
                        section: doc._id.section,
                        row: doc._id.row,
                        sales: doc.sales,
                        costs: doc.costs,
                        profit: doc.profit,
                        customer: doc._id.customer
                    });
                });
                setRows(r);
                setLoading(false);
            }).catch((err) => {
                if (err.message !== 'canceled') {
                    console.log(err);
                    setLoading(false);
                }
            });
        });
    }

    useEffect(() => {
        const controller = new AbortController();

        if (isConnected && current_broker) {
            if (previous_broker !== current_broker) {
                setLoading(true);
            }

            getData(controller);

            socket.on('data', () => { getData(controller); });
        }

        return () => {
            socket.off('data');
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnected, current_broker, startDate, endDate]);

    return (
        <>
            <Backdrop sx={{ color: '#fff', zIndex: 10000000 }} open={loading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Paper elevation={1}>
                <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="From"
                            value={startDate}
                            maxDate={endDate}
                            onChange={(newValue) => setStartDate(newValue)}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                        <DatePicker
                            label="To"
                            value={endDate}
                            minDate={startDate}
                            onChange={(newValue) => setEndDate(newValue)}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                    </LocalizationProvider>
                </Box>
                <div style={{ height: '72vh', width: '100%' }}>
                    <DataGridPro
                        slots={{
                            toolbar: GridToolbar,
                            footer: CustomFooter
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 }
                            }
                        }}
                        rows={rows}
                        columns={columns}
                        pagination
                    />
                </div>
            </Paper>
        </>
    );
}

export default HourlyRegister;
